import { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  FormControl,
  FormHelperText,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';
import { Iconify } from 'src/components';
import UserSearchDropdown from 'src/components/UserSearchDropdown';
import { useSelector } from 'src/redux/store';
import TreatmentFlowComponent from 'src/components/treatmentComponents/TreatmentFlowComponent';
import TreatmentEditPopover from 'src/pages/dashboard/tables/AllLeadTable/TreatmentEditPopover';
import { hasPractitionerRole } from '../../../utils/practiceUserRoles';

// Design tokens matching AISummaryModal
const colors = {
  primary: { main: '#00A76F', light: '#5BE49B', lighter: '#C8FAD6', dark: '#007867' },
  secondary: { main: '#8E33FF', lighter: '#EFD6FF' },
  success: { main: '#22C55E', light: '#77ED8B', lighter: '#D3FCD2', dark: '#118D57' },
  warning: { main: '#FFAB00', light: '#FFD666', lighter: '#FFF5CC', dark: '#B76E00' },
  error: { main: '#FF5630', light: '#FFAC82', lighter: '#FFE9D5', dark: '#B71D18' },
  info: { main: '#00B8D9', light: '#61F3F3', lighter: '#CAFDF5', dark: '#006C9C' },
  grey: { 100: '#F4F6F8', 200: '#DFE3E8', 300: '#C4CDD5', 500: '#637381', 700: '#212B36' },
};

// Payment options matching the original form
const PAYMENT_OPTIONS = [
  { label: 'No', value: 'No' },
  { label: 'Deposit', value: 'DEPOSIT' },
  { label: 'Consultation fee', value: 'CONSULTATION' },
  { label: 'Treatment', value: 'TREATMENT' },
];

// How to take the payment
const PAYMENT_METHODS = [
  { label: 'Send a payment link', value: 'PAYMENT_LINK' },
  { label: 'Take over the phone', value: 'PHONE' },
  { label: 'In practice', value: 'IN_PRACTICE' },
  { label: 'Email', value: 'EMAIL' },
];

// Default options if not loaded from schema
const DEFAULT_APPOINTMENT_METHODS = [
  { label: 'In Practice', value: 'IN_PRACTICE' },
  { label: 'Video Call', value: 'VIDEO_CALL' },
  { label: 'Phone', value: 'PHONE' },
];

const DEFAULT_APPOINTMENT_TYPES = [
  { label: 'Treatment', value: 'TREATMENT' },
  { label: 'Consultation', value: 'CONSULTATION' },
];

const DEFAULT_CONTACT_METHODS = [
  { label: 'Email', value: 'EMAIL' },
  { label: 'Phone', value: 'PHONE' },
  { label: 'SMS', value: 'SMS' },
  { label: 'Visited', value: 'VISITED' },
  { label: 'Social media', value: 'SOCIAL_MEDIA' },
  { label: 'Online chat', value: 'ONLINE_CHAT' },
  { label: 'Whatsapp', value: 'WHATSAPP' },
];

// Toggle button style
const toggleButtonStyle = {
  borderRadius: '50px !important',
  border: '1px solid #DFE3E8 !important',
  px: 2,
  py: 1,
  mr: 1,
  mb: 1,
  textTransform: 'none',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: colors.grey[700],
  '&.Mui-selected': {
    bgcolor: colors.primary.lighter,
    borderColor: `${colors.primary.main} !important`,
    color: colors.primary.dark,
    '&:hover': { bgcolor: colors.primary.lighter },
  },
};

/**
 * Helper to get treatment name from treatment object
 * Matches the implementation in CallScriptV2.jsx
 * treatment can be: string | { name: string } | { treatment_name: string }
 */
const getTreatmentName = (treatment) => {
  if (!treatment) return '';
  if (typeof treatment === 'string') return treatment;
  return treatment.name || treatment.treatment_name || '';
};

/**
 * AIBookingWizard - AI-powered appointment booking wizard
 * Pre-fills appointment details based on AI recommendations from call analysis
 * Matches the full booking form: treatment, price, date, booked with, assigned to,
 * method, type, contact method, payment options, and notes
 */
const AIBookingWizard = memo(({
  open,
  onClose,
  onBook,
  onTreatmentChange,
  aiRecommendation = {},
  availability = '',
  leadInfo = {},
  treatments = [],
  practiceUsers = [],
  currentUser = null,
  isBooking = false,
}) => {
  // Get schema options from Redux
  const {
    contactMethod: schemaContactMethods,
    appointmentType: schemaAppointmentTypes,
    appointmentMethod: schemaAppointmentMethods,
  } = useSelector((state) => state.schema);

  // Use schema options if available, otherwise use defaults
  const appointmentMethods = schemaAppointmentMethods?.length > 0 ? schemaAppointmentMethods : DEFAULT_APPOINTMENT_METHODS;
  const appointmentTypes = schemaAppointmentTypes?.length > 0 ? schemaAppointmentTypes : DEFAULT_APPOINTMENT_TYPES;
  const contactMethods = schemaContactMethods?.length > 0 ? schemaContactMethods : DEFAULT_CONTACT_METHODS;

  // Form state with AI pre-filled values
  const [formData, setFormData] = useState({
    treatment: '',
    treatmentId: '',
    treatmentPrice: '',
    teethAmount: null,
    appointmentType: 'CONSULTATION',
    appointmentMethod: 'IN_PRACTICE',
    contactMethod: 'PHONE',
    paymentOption: 'No',
    paymentAmount: '',
    paymentMethod: '',
    datetime: null,
    notes: '',
  });

  // Booked with state (user search dropdown)
  const [bookedWith, setBookedWith] = useState([]);
  // Assigned to state (system task)
  const [assignedTo, setAssignedTo] = useState([]);

  const [error, setError] = useState(null);

  // Treatment popover state
  const [treatmentPopoverOpen, setTreatmentPopoverOpen] = useState(false);
  const [treatmentAnchorEl, setTreatmentAnchorEl] = useState(null);
  const treatmentButtonRef = useRef(null);

  // Normalize practiceUsers to always be an array
  const usersList = useMemo(() =>
    Array.isArray(practiceUsers) ? practiceUsers : (practiceUsers?.results || []),
    [practiceUsers]
  );

  // Helper to get tomorrow's date at 10am
  const getTomorrowAt10am = useCallback(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);
    return tomorrow;
  }, []);

  // Initialize form with AI recommendations and lead data
  useEffect(() => {
    if (open) {
      const rec = aiRecommendation || {};

      // Use first lead treatment by default (we already have treatments from lead data)
      // AI recommendation can override if it matches
      // Structure of lead_treatments: [{ id, treatment (string or object), price, custom_price }]
      let selectedTreatment = treatments?.[0] || null;
      if (rec.treatment && treatments?.length > 0) {
        const matchedTreatment = treatments?.find(t =>
          getTreatmentName(t.treatment).toLowerCase().includes(rec.treatment.toLowerCase())
        );
        if (matchedTreatment) {
          selectedTreatment = matchedTreatment;
        }
      }

      // Get treatment name using helper (handles both string and object cases)
      const treatmentName = getTreatmentName(selectedTreatment?.treatment);
      const treatmentId = selectedTreatment?.id || '';
      const treatmentPrice = selectedTreatment?.price || selectedTreatment?.custom_price || '';
      const teethAmount = selectedTreatment?.leadtreatment_specification?.teeth_count ?? null;

      // Find matching staff member for "booked with" from AI recommendation
      // Priority: 1) AI-suggested practitioner, 2) Random practitioner, 3) First user
      let selectedBookedWith = [];

      // First, filter to get only practitioners (Practitioner as any of their roles)
      const practitioners = usersList?.filter((u) => hasPractitionerRole(u)) || [];

      // If AI recommended a specific practitioner (patient preference from call)
      if (rec.bookedWith && practitioners.length > 0) {
        const bookedWithLower = rec.bookedWith.toLowerCase();
        // Search among practitioners for the AI-suggested name
        const matchedPractitioner = practitioners.find(u => {
          const firstName = (u.first_name || u.user?.first_name || '').toLowerCase();
          const lastName = (u.last_name || u.user?.last_name || '').toLowerCase();
          const fullName = `${firstName} ${lastName}`.trim();
          // Match if the name contains the AI suggestion or vice versa
          return fullName.includes(bookedWithLower) ||
                 bookedWithLower.includes(firstName) ||
                 bookedWithLower.includes(lastName);
        });
        if (matchedPractitioner) {
          selectedBookedWith = [matchedPractitioner];
        }
      }

      // If no AI match found, randomly select a practitioner
      if (selectedBookedWith.length === 0 && practitioners.length > 0) {
        const randomIndex = Math.floor(Math.random() * practitioners.length);
        selectedBookedWith = [practitioners[randomIndex]];
      }

      // Fallback: if no practitioners, use first user from the list
      if (selectedBookedWith.length === 0 && usersList?.length > 0) {
        selectedBookedWith = [usersList[0]];
      }

      // Default assigned to current user in session
      // Match by email since currentUser from useAuth() has different structure than practiceUsers
      let selectedAssignedTo = [];
      if (currentUser && usersList?.length > 0) {
        const currentUserEmail = currentUser.email?.toLowerCase();
        const currentUserInList = usersList.find(u =>
          u.user?.email?.toLowerCase() === currentUserEmail ||
          u.email?.toLowerCase() === currentUserEmail ||
          u.id === currentUser.id
        );
        if (currentUserInList) {
          selectedAssignedTo = [currentUserInList];
        }
      }

      // Parse suggested date/time from AI - default to tomorrow at 10am if not specified
      // Check multiple sources: suggestedDate, suggestedDateTime, and availability string
      let suggestedDateTime = getTomorrowAt10am(); // Default to tomorrow at 10am

      // Helper function to try parsing a date string
      const tryParseDate = (dateInput, timeStr = '10:00') => {
        if (!dateInput) return null;
        try {
          // Try parsing with moment for more flexible date formats
          // Handles: "2026-01-19", "19/01/2026", "January 19, 2026", "19th January 2026", "25th Jan", etc.
          const momentDate = moment(dateInput, [
            'YYYY-MM-DD',
            'DD/MM/YYYY',
            'DD-MM-YYYY',
            'MMMM D, YYYY',
            'D MMMM YYYY',
            'Do MMMM YYYY',
            'Do MMMM',
            'D MMMM',
            'MMMM Do',
            'MMMM D',
            'Do MMM',
            'D MMM',
            'MMM Do',
            'MMM D',
            'YYYY-MM-DDTHH:mm',
            'DD/MM/YYYY HH:mm',
            moment.ISO_8601,
          ], true);

          if (momentDate.isValid()) {
            // If year wasn't in the string, moment defaults to current year
            // If the date is in the past, assume next year
            if (momentDate.isBefore(moment(), 'day')) {
              momentDate.add(1, 'year');
            }
            // If no time in the original string, add the suggested time
            if (!dateInput.includes(':') && timeStr) {
              const [hours, minutes] = timeStr.split(':');
              momentDate.hours(parseInt(hours, 10) || 10);
              momentDate.minutes(parseInt(minutes, 10) || 0);
            }
            const parsedDate = momentDate.toDate();
            if (!Number.isNaN(parsedDate.getTime()) && parsedDate > new Date()) {
              return parsedDate;
            }
          }
        } catch (e) {
          // Parsing failed
        }
        return null;
      };

      // Try parsing from suggestedDate/suggestedDateTime first
      if (rec.suggestedDate || rec.suggestedDateTime) {
        const dateInput = rec.suggestedDateTime || rec.suggestedDate;
        const timeStr = rec.suggestedTime || '10:00';
        const parsed = tryParseDate(dateInput, timeStr);
        if (parsed) {
          suggestedDateTime = parsed;
        }
      }

      // If no date from suggestedDate, try parsing from availability string
      // availability might be like "Available on 25th Jan", "book on 25th January", "next Monday", etc.
      if (suggestedDateTime.getTime() === getTomorrowAt10am().getTime() && availability) {
        // Extract potential date patterns from availability string
        // Look for patterns like "25th Jan", "January 25", "25/01", etc.
        const datePatterns = [
          /(\d{1,2}(?:st|nd|rd|th)?\s+(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?))/i,
          /((?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2}(?:st|nd|rd|th)?)/i,
          /(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)/,
          /(\d{1,2}-\d{1,2}(?:-\d{2,4})?)/,
        ];

        // Use .some() to find the first matching pattern and parse the date
        datePatterns.some((pattern) => {
          const match = availability.match(pattern);
          if (match) {
            const parsed = tryParseDate(match[1], rec.suggestedTime || '10:00');
            if (parsed) {
              suggestedDateTime = parsed;
              return true; // Stop iterating
            }
          }
          return false;
        });
      }

      // Determine AI-recommended payment option
      let paymentOption = 'No';
      if (rec.paymentOption) {
        const normalizedPayment = rec.paymentOption.toLowerCase();
        if (normalizedPayment.includes('deposit')) paymentOption = 'DEPOSIT';
        else if (normalizedPayment.includes('consultation')) paymentOption = 'CONSULTATION';
        else if (normalizedPayment.includes('treatment') || normalizedPayment.includes('full')) paymentOption = 'TREATMENT';
      }

      // Determine payment amount based on treatment price and payment option
      let paymentAmount = '';
      if (paymentOption !== 'No') {
        if (paymentOption === 'TREATMENT') {
          paymentAmount = treatmentPrice; // Full treatment amount
        } else if (paymentOption === 'DEPOSIT') {
          // Default deposit is usually 10-20% or a fixed amount
          paymentAmount = treatmentPrice ? Math.round(parseFloat(treatmentPrice) * 0.2).toString() : '';
        } else if (paymentOption === 'CONSULTATION') {
          paymentAmount = rec.consultationFee || '50'; // Default consultation fee
        }
      }

      setFormData({
        treatment: treatmentName,
        treatmentId,
        treatmentPrice: treatmentPrice?.toString() || '',
        teethAmount,
        appointmentType: rec.appointmentType || 'CONSULTATION',
        appointmentMethod: rec.appointmentMethod || 'IN_PRACTICE',
        contactMethod: rec.contactMethod || 'PHONE',
        paymentOption,
        paymentAmount,
        paymentMethod: rec.paymentMethod || 'PAYMENT_LINK',
        datetime: suggestedDateTime,
        notes: rec.notes || '',
      });

      setBookedWith(selectedBookedWith);
      setAssignedTo(selectedAssignedTo);
      setError(null);
    }
  }, [open, aiRecommendation, availability, treatments, usersList, currentUser, getTomorrowAt10am]);

  // Handle field changes
  const handleChange = useCallback((field) => (event) => {
    const value = event?.target?.value ?? event;
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  }, []);

  // Handle toggle button changes
  const handleToggleChange = useCallback((field) => (event, value) => {
    if (value !== null) {
      setFormData(prev => ({ ...prev, [field]: value }));
      setError(null);
    }
  }, []);

  // Treatment popover handlers
  const handleTreatmentClick = useCallback((event) => {
    setTreatmentAnchorEl(event.currentTarget);
    setTreatmentPopoverOpen(true);
  }, []);

  const handleTreatmentClose = useCallback(() => {
    setTreatmentPopoverOpen(false);
    setTreatmentAnchorEl(null);
  }, []);

  // Handle treatment selection from popover (saves to lead via callback)
  const handleSaveTreatment = useCallback((treatmentInfo) => {
    // Note: treatmentInfo.selectedTreatmentId is the PRACTICE treatment catalog ID
    // We should NOT pass this to onTreatmentChange for API calls
    // The parent component should use the existing lead_treatment ID
    const updatedData = {
      treatment: treatmentInfo.selectedTreatment || '',
      treatmentPrice: treatmentInfo.treatmentPrice || '',
      teethAmount: treatmentInfo.teethAmount || null,
    };

    setFormData(prev => ({
      ...prev,
      treatment: updatedData.treatment,
      treatmentPrice: updatedData.treatmentPrice?.toString() || '',
      // Keep the existing treatmentId (lead_treatment ID) - don't overwrite with practice treatment ID
    }));

    handleTreatmentClose();

    // Call the onTreatmentChange callback to save to lead
    // Don't pass treatmentId - the parent should use the existing lead_treatment ID
    if (onTreatmentChange) {
      onTreatmentChange({
        treatment: updatedData.treatment,
        treatmentPrice: updatedData.treatmentPrice,
        teethAmount: updatedData.teethAmount,
        leadId: leadInfo?.id,
      });
    }
  }, [handleTreatmentClose, onTreatmentChange, leadInfo?.id]);

  // Handle treatment price change (inline editing)
  const handleTreatmentAmountChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, treatmentPrice: value?.toString() || '' }));
  }, []);

  // Handle teeth count change
  const handleTeethChange = useCallback((event) => {
    const { value } = event.target;
    setFormData(prev => ({ ...prev, teethAmount: value }));
  }, []);

  // Handle booking submission
  const handleBook = useCallback(() => {
    // Validate required fields
    if (!formData.datetime) {
      setError('Please select an appointment date and time');
      return;
    }
    if (bookedWith.length === 0) {
      setError('Please select who the appointment is booked with');
      return;
    }
    if (!formData.appointmentMethod) {
      setError('Please select an appointment method');
      return;
    }
    if (!formData.appointmentType) {
      setError('Please select an appointment type');
      return;
    }
    if (!formData.contactMethod) {
      setError('Please select a contact method');
      return;
    }

    // Format datetime as expected by API
    const formattedDatetime = formData.datetime
      ? `${moment(formData.datetime).format('DD-MM-YYYY HH:mm:ss')}.0Z`
      : null;

    // Call the onBook callback with full form data
    onBook?.({
      lead: leadInfo?.id,
      lead_treatment: formData.treatmentId || leadInfo?.lead_treatments?.[0]?.id,
      datetime: formattedDatetime,
      method: formData.appointmentMethod,
      type: formData.appointmentType,
      contacted_via: formData.contactMethod,
      assignees: bookedWith.map(u => u.id),
      system_assignees: assignedTo.map(u => u.id),
      note: formData.notes,
      // Payment info for follow-up processing
      payment_option: formData.paymentOption,
      payment_amount: formData.paymentOption !== 'No' ? formData.paymentAmount : null,
      payment_method: formData.paymentOption !== 'No' ? formData.paymentMethod : null,
      // Treatment info
      treatment_name: formData.treatment,
      treatment_price: formData.treatmentPrice,
    });
  }, [formData, leadInfo, bookedWith, assignedTo, onBook]);

  const leadName = `${leadInfo?.first_name || ''} ${leadInfo?.last_name || ''}`.trim() || 'Patient';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          maxHeight: '95vh',
        }
      }}
      sx={{ zIndex: 1500 }}
    >
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${colors.secondary.main} 0%, #8b5cf6 50%, ${colors.info.main} 100%)`,
        px: 3,
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}>
        <Box sx={{
          width: 48,
          height: 48,
          borderRadius: '14px',
          bgcolor: 'rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Iconify icon="mdi:calendar-star" width={26} height={26} sx={{ color: '#fff' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>
            AI-Powered Booking
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)' }}>
            Pre-filled based on call analysis
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <Iconify icon="mdi:close" width={24} height={24} />
        </IconButton>
      </Box>

      {/* AI Confidence Badge */}
      <Box sx={{ px: 3, pt: 2 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          bgcolor: colors.success.lighter,
          borderRadius: '12px',
          border: `1px solid ${colors.success.light}`,
        }}>
          <Iconify icon="mdi:robot-happy" width={20} height={20} sx={{ color: colors.success.dark }} />
          <Typography sx={{ fontSize: '0.8125rem', color: colors.success.dark, fontWeight: 600, flex: 1 }}>
            AI has pre-filled recommended values based on your call with {leadName}
          </Typography>
          <Chip
            label="Review & Edit"
            size="small"
            sx={{
              bgcolor: colors.success.main,
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.6875rem',
            }}
          />
        </Box>
      </Box>

      {/* Form Content - Scrollable */}
      <Box sx={{ px: 3, py: 2, overflowY: 'auto', maxHeight: 'calc(95vh - 250px)' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        {/* Treatment Selection - uses same components as call script */}
        <Box sx={{ mb: 2.5 }} ref={treatmentButtonRef}>
          {treatmentPopoverOpen && (
            <TreatmentEditPopover
              treatment={formData.treatment || ''}
              leadTreatmentSpecification={{ teeth_count: formData.teethAmount }}
              open={treatmentPopoverOpen}
              anchorEl={treatmentAnchorEl}
              handleClickAwayLister={handleTreatmentClose}
              treatmentPrice={parseFloat(formData.treatmentPrice) || 0}
              handleSaveTreatment={handleSaveTreatment}
              isFlow
              placement="bottom-start"
            />
          )}
          <TreatmentFlowComponent
            treatmentData={formData.treatment}
            teethAmount={formData.teethAmount ? parseInt(formData.teethAmount, 10) : null}
            handleTreatment={handleTreatmentClick}
            amount={parseFloat(formData.treatmentPrice) || 0}
            amountHandleChange={handleTreatmentAmountChange}
            handleChangeTeeth={handleTeethChange}
          />
        </Box>

        {/* Date, Booked With, Assigned To Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2.5 }}>
          {/* Date Time Picker */}
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 0.75 }}>
              Appointment date (Required)
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                value={formData.datetime}
                onChange={(newValue) => {
                  setFormData(prev => ({ ...prev, datetime: newValue }));
                  setError(null);
                }}
                format="dd/MM/yyyy HH:mm"
                ampm={false}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    placeholder: 'DD/MM/YYYY hh:mm',
                    error: !formData.datetime && error?.includes('date'),
                    InputProps: {
                      sx: { bgcolor: '#fff' },
                    },
                  },
                  actionBar: {
                    actions: ['clear', 'today', 'accept'],
                  },
                  popper: {
                    sx: { zIndex: 1600 }, // Higher than Dialog's zIndex (1500)
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          {/* Booked With */}
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 0.75 }}>
              Booked with (Required)
            </Typography>
            <UserSearchDropdown
              name="bookedWith"
              label=""
              assignToState={bookedWith}
              setAssignTo={setBookedWith}
              error={bookedWith.length === 0 && error?.includes('booked')}
            />
          </Box>

          {/* Assigned To */}
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 0.75 }}>
              Assigned to (Used for system Task)
            </Typography>
            <UserSearchDropdown
              name="assignedTo"
              label=""
              assignToState={assignedTo}
              setAssignTo={setAssignedTo}
            />
          </Box>
        </Box>

        {/* Appointment Method */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 1 }}>
            Appointment method (Required)
          </Typography>
          <ToggleButtonGroup
            color="primary"
            exclusive
            value={formData.appointmentMethod}
            onChange={handleToggleChange('appointmentMethod')}
            sx={{ flexWrap: 'wrap' }}
          >
            {appointmentMethods.map((option) => (
              <ToggleButton key={option.value} value={option.value} sx={toggleButtonStyle}>
                <Iconify
                  icon={formData.appointmentMethod === option.value ? 'bi:check-circle-fill' : 'material-symbols:circle'}
                  width={18}
                  height={18}
                  sx={{ mr: 0.75, color: formData.appointmentMethod === option.value ? colors.primary.main : colors.grey[300] }}
                />
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {!formData.appointmentMethod && error?.includes('method') && (
            <FormHelperText error>Please select an appointment method</FormHelperText>
          )}
        </FormControl>

        {/* Appointment Type */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 1 }}>
            Appointment type (Required)
          </Typography>
          <ToggleButtonGroup
            color="primary"
            exclusive
            value={formData.appointmentType}
            onChange={handleToggleChange('appointmentType')}
            sx={{ flexWrap: 'wrap' }}
          >
            {appointmentTypes.map((option) => (
              <ToggleButton key={option.value} value={option.value} sx={toggleButtonStyle}>
                <Iconify
                  icon={formData.appointmentType === option.value ? 'bi:check-circle-fill' : 'material-symbols:circle'}
                  width={18}
                  height={18}
                  sx={{ mr: 0.75, color: formData.appointmentType === option.value ? colors.primary.main : colors.grey[300] }}
                />
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {!formData.appointmentType && error?.includes('type') && (
            <FormHelperText error>Please select an appointment type</FormHelperText>
          )}
        </FormControl>

        {/* Contact Method */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 1 }}>
            Contact method (Required)
          </Typography>
          <ToggleButtonGroup
            color="primary"
            exclusive
            value={formData.contactMethod}
            onChange={handleToggleChange('contactMethod')}
            sx={{ flexWrap: 'wrap' }}
          >
            {contactMethods.map((option) => (
              <ToggleButton key={option.value} value={option.value} sx={toggleButtonStyle}>
                <Iconify
                  icon={formData.contactMethod === option.value ? 'bi:check-circle-fill' : 'material-symbols:circle'}
                  width={18}
                  height={18}
                  sx={{ mr: 0.75, color: formData.contactMethod === option.value ? colors.primary.main : colors.grey[300] }}
                />
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {!formData.contactMethod && error?.includes('contact') && (
            <FormHelperText error>Please select a contact method</FormHelperText>
          )}
        </FormControl>

        {/* Payment Options */}
        <Box sx={{ bgcolor: colors.grey[100], borderRadius: '12px', p: 2, mb: 2 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: colors.grey[700], mb: 1.5 }}>
            Payment options
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: colors.grey[500], mb: 1.5 }}>
            Take a deposit or consultation fee for {formData.treatment || 'this treatment'}?
          </Typography>
          <ToggleButtonGroup
            color="primary"
            exclusive
            value={formData.paymentOption}
            onChange={handleToggleChange('paymentOption')}
            sx={{ flexWrap: 'wrap', mb: formData.paymentOption !== 'No' ? 2 : 0 }}
          >
            {PAYMENT_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value} sx={toggleButtonStyle}>
                <Iconify
                  icon={formData.paymentOption === option.value ? 'bi:check-circle-fill' : 'material-symbols:circle'}
                  width={18}
                  height={18}
                  sx={{ mr: 0.75, color: formData.paymentOption === option.value ? colors.primary.main : colors.grey[300] }}
                />
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Conditional payment fields - show when payment option is not "No" */}
          {formData.paymentOption !== 'No' && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.grey[200]}` }}>
              {/* Payment Amount */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 0.75 }}>
                  Enter {PAYMENT_OPTIONS.find(opt => opt.value === formData.paymentOption)?.label || 'Payment'} Amount
                </Typography>
                <TextField
                  fullWidth
                  value={formData.paymentAmount}
                  onChange={handleChange('paymentAmount')}
                  size="small"
                  placeholder="0.00"
                  sx={{ bgcolor: '#fff' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: colors.grey[500] }}>£</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Payment Method */}
              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 1 }}>
                  How would you like to take the payment?
                </Typography>
                <ToggleButtonGroup
                  color="primary"
                  exclusive
                  value={formData.paymentMethod}
                  onChange={handleToggleChange('paymentMethod')}
                  sx={{ flexWrap: 'wrap' }}
                >
                  {PAYMENT_METHODS.map((option) => (
                    <ToggleButton key={option.value} value={option.value} sx={toggleButtonStyle}>
                      <Iconify
                        icon={formData.paymentMethod === option.value ? 'bi:check-circle-fill' : 'material-symbols:circle'}
                        width={18}
                        height={18}
                        sx={{ mr: 0.75, color: formData.paymentMethod === option.value ? colors.primary.main : colors.grey[300] }}
                      />
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {/* Action indicator based on payment method */}
                {formData.paymentMethod && (() => {
                  const paymentIcons = {
                    PAYMENT_LINK: 'mdi:link-variant',
                    PHONE: 'mdi:phone',
                    EMAIL: 'mdi:email-fast',
                    IN_PRACTICE: 'mdi:store',
                  };
                  const paymentMessages = {
                    PAYMENT_LINK: `Payment link of £${formData.paymentAmount || '0'} will be sent via WhatsApp/SMS after booking`,
                    PHONE: `Collect £${formData.paymentAmount || '0'} payment over the phone`,
                    EMAIL: `Invoice for £${formData.paymentAmount || '0'} will be sent via email`,
                    IN_PRACTICE: `Patient will pay £${formData.paymentAmount || '0'} at the practice`,
                  };
                  return (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: colors.info.lighter, borderRadius: '8px', border: `1px solid ${colors.info.light}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Iconify
                          icon={paymentIcons[formData.paymentMethod] || 'mdi:cash'}
                          width={18}
                          height={18}
                          sx={{ color: colors.info.dark }}
                        />
                        <Typography sx={{ fontSize: '0.75rem', color: colors.info.dark, fontWeight: 500 }}>
                          {paymentMessages[formData.paymentMethod]}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          )}
        </Box>

        {/* Notes */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Appointment Notes"
          value={formData.notes}
          onChange={handleChange('notes')}
          placeholder="AI-generated notes from call analysis..."
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
            },
          }}
        />
      </Box>

      {/* Footer */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 3,
        py: 2,
        borderTop: `1px solid ${colors.grey[200]}`,
        bgcolor: colors.grey[100],
      }}>
        <Button
          onClick={onClose}
          sx={{
            py: 1.25,
            px: 3,
            color: colors.grey[700],
            fontWeight: 600,
            borderRadius: '10px',
            textTransform: 'none',
          }}
        >
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleBook}
          disabled={isBooking}
          startIcon={isBooking ? (
            <CircularProgress size={18} sx={{ color: '#fff' }} />
          ) : (
            <Iconify icon="mdi:calendar-check" width={20} height={20} />
          )}
          sx={{
            py: 1.25,
            px: 3,
            bgcolor: colors.success.main,
            color: '#fff',
            fontWeight: 700,
            borderRadius: '10px',
            textTransform: 'none',
            boxShadow: `0 4px 14px ${colors.success.main}40`,
            '&:hover': { bgcolor: colors.success.dark },
            '&:disabled': { bgcolor: colors.grey[300] },
          }}
        >
          {isBooking ? 'Booking...' : 'Confirm Booking'}
        </Button>
      </Box>
    </Dialog>
  );
});

AIBookingWizard.displayName = 'AIBookingWizard';

AIBookingWizard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBook: PropTypes.func.isRequired,
  onTreatmentChange: PropTypes.func,
  aiRecommendation: PropTypes.shape({
    recommended: PropTypes.bool,
    appointmentType: PropTypes.string,
    appointmentMethod: PropTypes.string,
    contactMethod: PropTypes.string,
    paymentOption: PropTypes.string,
    suggestedDate: PropTypes.string,
    suggestedTime: PropTypes.string,
    bookedWith: PropTypes.string,
    treatment: PropTypes.string,
    notes: PropTypes.string,
  }),
  availability: PropTypes.string,
  leadInfo: PropTypes.object,
  treatments: PropTypes.array,
  practiceUsers: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  currentUser: PropTypes.object,
  isBooking: PropTypes.bool,
};

export default AIBookingWizard;
